/**
 * AdminAnalytics.jsx  —  Master Admin Only
 * Charts: Top Medicines · Doctor-wise Patients · OPD Requests Trend
 * Default window: Last 3 months. Switchable to 7d / 30d / 90d / 180d.
 */

import { useEffect, useState, useCallback } from "react";
import api from "../../services/api";

/* ── Tiny bar component ── */
function Bar({ value, max, color }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div style={{ flex: 1, background: "#F1F5F9", borderRadius: 6, overflow: "hidden", height: 10 }}>
      <div
        style={{
          width: `${pct}%`,
          height: "100%",
          background: color,
          borderRadius: 6,
          transition: "width 0.6s cubic-bezier(.4,0,.2,1)",
        }}
      />
    </div>
  );
}

/* ── Spark line SVG ── */
function SparkLine({ data, valueKey, color }) {
  if (!data || data.length < 2) return null;
  const vals  = data.map(d => d[valueKey] || 0);
  const max   = Math.max(...vals, 1);
  const W = 280, H = 56, pad = 4;
  const pts = vals.map((v, i) => {
    const x = pad + (i / (vals.length - 1)) * (W - pad * 2);
    const y = H - pad - ((v / max) * (H - pad * 2));
    return `${x},${y}`;
  }).join(" ");
  const area = `M${pts.split(" ")[0]} L${pts} L${W - pad},${H} L${pad},${H} Z`;
  return (
    <svg width={W} height={H} style={{ overflow: "visible" }}>
      <defs>
        <linearGradient id={`sg-${color.replace("#","")}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#sg-${color.replace("#","")})`} />
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
}

/* ── Stat card ── */
function StatCard({ label, value, sub, color, icon }) {
  return (
    <div style={{
      background: "#fff",
      border: "1px solid #E8EDF3",
      borderRadius: 16,
      padding: "20px 24px",
      display: "flex",
      flexDirection: "column",
      gap: 6,
      boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 13, color: "#64748B", fontWeight: 500 }}>{label}</span>
        <span style={{ fontSize: 20 }}>{icon}</span>
      </div>
      <div style={{ fontSize: 32, fontWeight: 700, color: "#0F172A", lineHeight: 1.1 }}>{value ?? "—"}</div>
      {sub && <div style={{ fontSize: 12, color: "#94A3B8" }}>{sub}</div>}
      <div style={{ height: 3, background: color, borderRadius: 99, marginTop: 4, opacity: 0.7 }} />
    </div>
  );
}

/* ── Section card wrapper ── */
function Card({ title, children, action }) {
  return (
    <div style={{
      background: "#fff",
      border: "1px solid #E8EDF3",
      borderRadius: 16,
      padding: "24px",
      boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#0F172A" }}>{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

const WINDOWS = [
  { label: "7 days",    days: 7   },
  { label: "30 days",   days: 30  },
  { label: "3 months",  days: 90  },
  { label: "6 months",  days: 180 },
];

const DOCTOR_COLORS = ["#6366F1","#10B981","#F59E0B","#EF4444","#8B5CF6","#14B8A6","#F97316","#06B6D4"];
const MED_COLOR     = "#10B981";
const OPD_COLORS    = { Online: "#6366F1", Appointment: "#10B981", Enquiry: "#F59E0B" };

export default function AdminAnalytics() {
  const [days,    setDays]    = useState(90);
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const load = useCallback(async (d) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/analytics/summary?days=${d}`);
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.message || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(days); }, [days, load]);

  const fmt = n => n?.toLocaleString() ?? "0";

  /* ── OPD trend: group by label ── */
  const opdLabels = data?.opdTrend?.map(w => {
    const d = new Date(w.week);
    return `${d.getDate()} ${d.toLocaleString("default",{month:"short"})}`;
  }) || [];

  const maxOpdTotal = Math.max(...(data?.opdTrend?.map(w => w.total) || [1]), 1);
  const maxMed      = Math.max(...(data?.topMedicines?.map(m => m.count) || [1]), 1);
  const maxDoc      = Math.max(...(data?.doctorWisePatients?.map(d => d.patientCount) || [1]), 1);

  return (
    <div style={{ padding: "28px 24px", maxWidth: 1100, margin: "0 auto", fontFamily: "'Inter', 'Segoe UI', sans-serif" }}>

      {/* ── Header ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 28 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: "#0F172A" }}>Analytics</h1>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "#64748B" }}>
            {data ? `${new Date(data.window.start).toLocaleDateString()} — ${new Date(data.window.end).toLocaleDateString()}` : "Loading…"}
          </p>
        </div>

        {/* Window switcher */}
        <div style={{ display: "flex", gap: 6, background: "#F1F5F9", borderRadius: 10, padding: 4 }}>
          {WINDOWS.map(w => (
            <button
              key={w.days}
              onClick={() => setDays(w.days)}
              style={{
                border: "none", cursor: "pointer", borderRadius: 7, padding: "6px 14px",
                fontSize: 13, fontWeight: 600,
                background: days === w.days ? "#fff" : "transparent",
                color: days === w.days ? "#0F172A" : "#64748B",
                boxShadow: days === w.days ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                transition: "all 0.15s",
              }}
            >{w.label}</button>
          ))}
        </div>
      </div>

      {/* ── Error ── */}
      {error && (
        <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 10, padding: "14px 18px", color: "#DC2626", marginBottom: 24, fontSize: 14 }}>
          ⚠️ {error}
        </div>
      )}

      {/* ── Loading skeleton ── */}
      {loading && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))", gap: 16, marginBottom: 24 }}>
          {[1,2,3,4].map(i => (
            <div key={i} style={{ height: 120, background: "#F1F5F9", borderRadius: 16, animation: "pulse 1.5s infinite" }} />
          ))}
        </div>
      )}

      {data && !loading && (<>

        {/* ── Summary stats ── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))", gap: 16, marginBottom: 24 }}>
          <StatCard label="Total Prescriptions" value={fmt(data.summary.totalPrescriptions)} sub="in selected period" color="#6366F1" icon="📋" />
          <StatCard label="Unique Patients"      value={fmt(data.summary.uniquePatients)}     sub="seen by doctors"    color="#10B981" icon="🧑‍⚕️" />
          <StatCard label="OPD Requests"         value={fmt(data.summary.totalRequests)}       sub="online + walk-in"  color="#F59E0B" icon="📅" />
          <StatCard label="Active Doctors"        value={fmt(data.summary.activeDoctors)}       sub="with prescriptions" color="#EF4444" icon="👨‍⚕️" />
        </div>

        {/* ── Two column row ── */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>

          {/* Top Medicines */}
          <Card title="💊 Top Medicines in Prescriptions">
            {data.topMedicines.length === 0 ? (
              <p style={{ color: "#94A3B8", fontSize: 13 }}>No medicine data found in this period.</p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {data.topMedicines.slice(0, 10).map((m, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: "#94A3B8", width: 18, textAlign: "right" }}>{i + 1}</span>
                    <span style={{ fontSize: 13, color: "#1E293B", flex: "0 0 160px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textTransform: "capitalize" }} title={m.name}>{m.name}</span>
                    <Bar value={m.count} max={maxMed} color={MED_COLOR} />
                    <span style={{ fontSize: 12, fontWeight: 700, color: MED_COLOR, width: 28, textAlign: "right" }}>{m.count}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Doctor-wise patients */}
          <Card title="👨‍⚕️ Doctor-wise Patient Count">
            {data.doctorWisePatients.length === 0 ? (
              <p style={{ color: "#94A3B8", fontSize: 13 }}>No prescriptions found in this period.</p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {data.doctorWisePatients.map((d, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: "50%",
                      background: DOCTOR_COLORS[i % DOCTOR_COLORS.length],
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: "#fff", fontWeight: 700, fontSize: 13, flexShrink: 0,
                    }}>
                      {d.doctorName?.[0]?.toUpperCase() || "D"}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#1E293B", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        Dr. {d.doctorName}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                        <Bar value={d.patientCount} max={maxDoc} color={DOCTOR_COLORS[i % DOCTOR_COLORS.length]} />
                        <span style={{ fontSize: 12, fontWeight: 700, color: DOCTOR_COLORS[i % DOCTOR_COLORS.length], width: 28, textAlign: "right" }}>
                          {d.patientCount}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* ── OPD Requests Trend ── */}
        <Card title="📅 OPD Requests Trend (by week)">
          {data.opdTrend.length === 0 ? (
            <p style={{ color: "#94A3B8", fontSize: 13 }}>No request data found in this period.</p>
          ) : (<>
            {/* Legend */}
            <div style={{ display: "flex", gap: 20, marginBottom: 16 }}>
              {Object.entries(OPD_COLORS).map(([type, color]) => (
                <div key={type} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ width: 10, height: 10, borderRadius: "50%", background: color }} />
                  <span style={{ fontSize: 12, color: "#64748B" }}>{type}</span>
                </div>
              ))}
            </div>

            {/* Stacked bar chart */}
            <div style={{ overflowX: "auto" }}>
              <div style={{ display: "flex", alignItems: "flex-end", gap: 8, minWidth: data.opdTrend.length * 52, height: 160, padding: "0 4px" }}>
                {data.opdTrend.map((w, i) => {
                  const total = w.total || 0;
                  const heightPct = maxOpdTotal > 0 ? (total / maxOpdTotal) * 140 : 0;
                  return (
                    <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", flex: 1, minWidth: 44 }}>
                      <span style={{ fontSize: 10, color: "#64748B", marginBottom: 4 }}>{total > 0 ? total : ""}</span>
                      <div style={{ width: "100%", display: "flex", flexDirection: "column", borderRadius: 6, overflow: "hidden", height: heightPct || 2, minHeight: total > 0 ? 4 : 2, background: total === 0 ? "#F1F5F9" : "transparent" }}>
                        {Object.entries(OPD_COLORS).map(([type, color]) => {
                          const val  = w[type] || 0;
                          const hpct = total > 0 ? (val / total) * 100 : 0;
                          return hpct > 0 ? (
                            <div key={type} style={{ width: "100%", height: `${hpct}%`, background: color, minHeight: 3 }} title={`${type}: ${val}`} />
                          ) : null;
                        })}
                      </div>
                      <span style={{ fontSize: 9, color: "#94A3B8", marginTop: 6, textAlign: "center", lineHeight: 1.2 }}>
                        {opdLabels[i]}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Type breakdown table */}
            <div style={{ marginTop: 20, display: "flex", gap: 16, flexWrap: "wrap" }}>
              {Object.entries(OPD_COLORS).map(([type, color]) => {
                const total = data.opdTrend.reduce((s, w) => s + (w[type] || 0), 0);
                return (
                  <div key={type} style={{ background: "#F8FAFC", borderRadius: 10, padding: "12px 18px", flex: 1, minWidth: 100, borderLeft: `3px solid ${color}` }}>
                    <div style={{ fontSize: 12, color: "#64748B" }}>{type}</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: "#0F172A" }}>{total}</div>
                  </div>
                );
              })}
            </div>
          </>)}
        </Card>

      </>)}

      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }
      `}</style>
    </div>
  );
}
